<template>
  <div class="modal fade" id="documentTemplateModal" tabindex="-1">
    <div class="modal-dialog modal-xl">
      <div class="modal-content" :key="uniqueKey">
        <div class="modal-header">
          <h5 class="modal-title">
            {{ modalTitle }}
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Fechar"></button>
        </div>

        <div class="modal-body">
          <form @submit.prevent="submit">
            <div class="row">
              <div class="col-md-8">
                <div class="row">
                  <div class="col-md-12">
                    <label class="form-label required">Nome do modelo</label>
                    <input type="text" class="form-control form-control-solid" required
                           placeholder="Ex: Atestado de comparecimento" v-model="form.doctempl_name"
                           :disabled="readonly"/>
                    <small class="text-danger" v-if="formErrors.doctempl_name">{{ formErrors.doctempl_name[0] }}</small>
                  </div>

                  <div class="col-md-12 mt-3">
                    <label class="form-label required">Conteúdo</label>
                    <QuillEditor
                      ref="quillEditor"
                      height="360"
                      :value="form.doctempl_body"
                      :options="editorOptions"
                      class="form-control-solid"
                      @change="onEditorChange"
                    />
                    <small class="text-danger" v-if="formErrors.doctempl_body">{{ formErrors.doctempl_body[0] }}</small>
                  </div>

                  <div class="col-md-4 mt-3">
                    <label class="form-label">Ativo?</label>
                    <div class="form-check form-switch">
                      <input class="form-check-input" type="checkbox" role="switch"
                             v-model="form.doctempl_active" :disabled="readonly">
                    </div>
                  </div>
                  <div class="col-md-4 mt-3">
                    <label class="form-label">Preencher com IA?</label>
                    <div class="form-check form-switch">
                      <input class="form-check-input" type="checkbox" role="switch"
                             v-model="form.doctempl_ai_fill" :disabled="readonly">
                    </div>
                  </div>
                  <div class="col-md-12 mt-3" v-if="form.doctempl_ai_fill">
                    <label class="form-label">Prompt da IA</label>
                    <textarea class="form-control form-control-solid" rows="5"
                              v-model="form.doctempl_ai_prompt"
                              :disabled="readonly"
                              placeholder="Ex.: Foque em evolução clínica objetiva; destaque queixa, conduta e retorno."></textarea>
                    <small class="text-muted">
                      Orientação específica deste modelo. As regras gerais de segurança (não inventar dados, manter HTML) continuam valendo.
                    </small>
                  </div>
                </div>
              </div>

              <div class="col-md-4" v-if="!readonly">
                <div class="card bg-light">
                  <div class="card-header">
                    <h6 class="card-title mb-0">
                      <i class="bi bi-code-square me-2"></i>
                      Variáveis disponíveis
                    </h6>
                    <small class="text-muted">Clique para inserir no texto</small>
                  </div>
                  <div class="card-body p-0" style="max-height: 460px; overflow-y: auto;">
                    <div class="accordion accordion-flush" id="docVariablesAccordion">
                      <div class="accordion-item" v-for="(vars, category) in variables" :key="category">
                        <h2 class="accordion-header">
                          <button class="accordion-button collapsed py-2 px-3" type="button"
                                  data-bs-toggle="collapse"
                                  :data-bs-target="'#docVar' + sanitizeId(category)">
                            <i class="bi bi-folder me-2"></i>
                            {{ category }}
                          </button>
                        </h2>
                        <div :id="'docVar' + sanitizeId(category)" class="accordion-collapse collapse"
                             data-bs-parent="#docVariablesAccordion">
                          <div class="accordion-body py-2 px-3">
                            <div v-for="(variable, index) in vars" :key="index"
                                 class="mb-2 p-2 bg-white rounded border cursor-pointer"
                                 @click="insertVariable(variable.code)"
                                 :title="variable.description">
                              <div class="d-flex align-items-center">
                                <i class="bi bi-plus-circle text-success me-2"></i>
                                <span class="fw-medium">{{ variable.label }}</span>
                              </div>
                              <small class="text-muted ps-4">{{ variable.code }}</small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="modal-footer mt-3">
              <button type="button" class="btn btn-light" data-bs-dismiss="modal">
                {{ readonly ? 'Fechar' : 'Cancelar' }}
              </button>
              <button type="submit" class="btn btn-primary" v-if="!readonly">
                {{ submitLabel }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import http from "@/helpers/http";
import Alert from "@/helpers/alert";
import 'quill/dist/quill.core.css';
import 'quill/dist/quill.snow.css';
import 'quill/dist/quill.bubble.css';
import QuillEditor from "vue-quill-editor/src/editor.vue";

export default {
  name: "DocumentTemplateModal",
  props: {
    template: {type: Object, default: null},
    professionalId: {type: [Number, String], required: true},
    readonly: {type: Boolean, default: false},
  },
  components: {
    QuillEditor,
  },
  data() {
    return {
      form: {
        doctempl_name: '',
        doctempl_body: '',
        doctempl_active: true,
        doctempl_ai_fill: false,
        doctempl_ai_prompt: '',
      },
      formErrors: {},
      uniqueKey: Date.now().toString(),
      variables: {
        Paciente: [
          {code: '{{patient_name}}', label: 'Nome', description: 'Nome do paciente'},
          {code: '{{patient_cpf}}', label: 'CPF', description: 'CPF formatado'},
        ],
        Atendimento: [
          {code: '{{current_date}}', label: 'Data atual', description: 'Data no fuso do local'},
          {code: '{{current_time}}', label: 'Hora atual', description: 'Hora no fuso do local'},
          {code: '{{sched_arrive_time}}', label: 'Chegada', description: 'Horário de chegada'},
          {code: '{{cids}}', label: 'CIDs', description: 'CIDs do prontuário'},
        ],
        Profissional: [
          {code: '{{prof_name}}', label: 'Nome', description: 'Nome do profissional'},
          {code: '{{prof_council}}', label: 'Conselho', description: 'Número do conselho'},
          {code: '{{kind_acronym}}', label: 'Sigla do conselho', description: 'Ex: CRM, CRP'},
        ],
        Local: [
          {code: '{{place_name}}', label: 'Nome do local', description: 'Nome do estabelecimento'},
        ],
      },
    };
  },
  watch: {
    template: {
      immediate: true,
      handler() {
        this.populateModal();
        this.uniqueKey = Date.now().toString();
      },
    },
  },
  computed: {
    isEditMode() {
      return !!this.template?.id_doctempl;
    },
    modalTitle() {
      if (this.readonly) {
        return 'Visualizar Modelo de Documento';
      }

      if (this.isCloneDraft) {
        return 'Duplicar Modelo de Documento';
      }

      return this.isEditMode ? 'Editar Modelo de Documento' : 'Novo Modelo de Documento';
    },
    isCloneDraft() {
      return !!this.template && !this.template.id_doctempl && !!this.template.doctempl_body;
    },
    submitLabel() {
      if (this.isCloneDraft) {
        return 'Salvar cópia';
      }

      return this.isEditMode ? 'Alterar' : 'Cadastrar';
    },
    editorOptions() {
      if (this.readonly) {
        return {
          readOnly: true,
          modules: {toolbar: false},
        };
      }

      return {
        placeholder: 'Digite o conteúdo do documento...',
        modules: {
          toolbar: [
            [{'size': ['small', false, 'large', 'huge']}],
            ['bold', 'italic', 'underline', 'strike'],
            [{'list': 'ordered'}, {'list': 'bullet'}],
            [{'align': []}],
            ['clean'],
          ],
        },
      };
    },
  },
  methods: {
    populateModal() {
      this.formErrors = {};
      if (!this.template) {
        this.form = {
          doctempl_name: '',
          doctempl_body: '',
          doctempl_active: true,
          doctempl_ai_fill: false,
          doctempl_ai_prompt: '',
        };
        return;
      }

      this.form = {
        doctempl_name: this.template.doctempl_name || '',
        doctempl_body: this.template.doctempl_body || '',
        doctempl_active: this.template.doctempl_active ?? true,
        doctempl_ai_fill: !!this.template.doctempl_ai_fill,
        doctempl_ai_prompt: this.template.doctempl_ai_prompt || '',
      };
    },
    sanitizeId(text) {
      return String(text).replace(/[^a-zA-Z0-9]/g, '');
    },
    onEditorChange(event) {
      if (this.readonly) {
        return;
      }
      this.form.doctempl_body = event.html;
    },
    insertVariable(variable) {
      if (this.readonly) {
        return;
      }
      const quill = this.$refs.quillEditor?.quill;
      if (!quill) {
        this.form.doctempl_body = (this.form.doctempl_body || '') + variable;
        return;
      }

      const selection = quill.getSelection(true);
      const index = selection ? selection.index : quill.getLength();
      quill.insertText(index, variable);
      quill.setSelection(index + variable.length);
    },
    submit() {
      if (this.readonly) {
        return;
      }

      if (!this.form.doctempl_name || !this.form.doctempl_body) {
        Alert.toastError('Preencha o nome e o conteúdo do modelo.');
        return;
      }

      this.formErrors = {};
      const request = this.isEditMode
        ? http.put(`/professionals/${this.professionalId}/document-templates/${this.template.id_doctempl}`, this.form)
        : http.post(`/professionals/${this.professionalId}/document-templates`, this.form);

      request.then(() => {
        Alert.toastSuccess(this.isEditMode ? 'Modelo atualizado com sucesso.' : 'Modelo cadastrado com sucesso.');
        bootstrap.Modal.getInstance(document.getElementById('documentTemplateModal'))?.hide();
        this.$emit('saved');
      }).catch((e) => {
        Alert.toastError(e.response?.data?.message ?? e.response?.data);
        if (e.response?.data?.errors) {
          this.formErrors = e.response.data.errors;
        }
      });
    },
  },
};
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
